import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteRelation, fetchRelations } from "../../../../../api/application/relation";
import { fetchTables } from "../../../../../api/application/table";
import type { Relation } from "../../../../../api/infrastructure/relation.formatter";
import type { APITable } from "../../../../../api/infrastructure/table.formatter";
import { ClientError } from "../../../../../api/shared/http-client";
import { appLog } from "../../../../../context";
import { useGetDatabaseCreationInfo } from "../../../../../store/store-hooks";
import { getUniqueId } from "../../../../shared/generate-unique-id";
import { showToast } from "../../../../shared/global-tools/toast-system";
import Icon from "../../../utils/Icons/Icon";
import NoDataMessage from "../../../utils/NoDataMessage/NoDataMessage";
import Spinner from "../../../utils/Spinner/Spinner";
import type { CreateDatabaseStepProps } from "../CreateDatabaseStepOne/CreateDatabaseStepOne";
import "./CreateDatabaseStepThree.scss";
import RelationCard from "./RelationCard/RelationCard";

export default function CreateDatabaseStepThree({
  navigateToStep,
  referenceId,
}: CreateDatabaseStepProps & { referenceId?: number }) {
  const { getDatabaseCreationInfo, clearDatabaseCreationInfo } = useGetDatabaseCreationInfo();
  const databaseCreationInfo = getDatabaseCreationInfo();
  const [relations, setRelations] = useState<Relation[]>([]);
  const [tables, setTables] = useState<APITable[]>([]);
  const [loadingRelations, setLoadingRelations] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const wrapAsync = useWrapAsync();
  const navigate = useNavigate();

  const handleDatabaseCreation = wrapAsync(async () => {
    if (!databaseCreationInfo) return;
    try {
      showToast(referenceId ? "Référentiel modifié avec succès" : "Référentiel créé", {
        severity: "success",
      });
      clearDatabaseCreationInfo();
      navigate(`/app/databases/${databaseCreationInfo.referenceId}/view`);
    } catch (error) {
      appLog.error(error);
      if (error instanceof ClientError) {
        showToast(error.message, {
          severity: "error",
          sticky: true,
        });
      } else {
        showToast("Echec création référentiel", { severity: "error", sticky: false });
      }
    }
  });

  const handleAddRelation = () => {
    if (tables.length === 0) return;
    relations.unshift({
      id: `temp_${getUniqueId()}`,
      type: "one_to_one",
      sourceColumn: "",
      targetColumn: "",
      sourceTableId: "",
      targetTableId: "",
    });
    setRelations([...relations]);
  };

  const performRelationDeletion = async (relationId: string) => {
    if (!databaseCreationInfo) return;
    if (relationId.includes("temp_")) {
      setRelations(relations.filter((r) => r.id !== relationId));
      return;
    }
    await deleteRelation(databaseCreationInfo.referenceId, Number(relationId));
  };

  const loadRelations = wrapAsync(async () => {
    if (!databaseCreationInfo) return;
    try {
      setLoadingRelations(true);
      const res = await fetchRelations(databaseCreationInfo.referenceId);
      setRelations([...res]);
    } catch (error) {
      console.error("Error fetching relations:", error);
    } finally {
      setLoadingRelations(false);
    }
  });

  const loadTables = wrapAsync(async () => {
    if (!databaseCreationInfo) return;
    try {
      setLoadingTables(true);
      const res = await fetchTables(databaseCreationInfo.referenceId);
      setTables([...res]);
    } catch (error) {
      console.error("Error fetching reference:", error);
    } finally {
      setLoadingTables(false);
    }
  });

  useEffect(() => {
    if (!databaseCreationInfo) {
      navigateToStep(1);
      return;
    }
    loadRelations();
    loadTables();
  }, []);

  if (!databaseCreationInfo) return;
  if (loadingRelations || loadingTables) return <Spinner />;
  if (tables.length === 0) {
    return <NoDataMessage message="Veuillez ajouter des tables pour configurer des relations." />;
  }
  return (
    <div className="CreateDatabaseStepThree">
      <div className="CreateDatabaseStepThree-header">
        <h2 className="CreateDatabaseStepThree-title">Configuration des relations entre tables</h2>

        <Button
          className="Btn success sm"
          label="Ajouter une relation"
          icon={<Icon name="relationship" />}
          disabled={
            relations.length > 0 &&
            relations.some(
              (r) =>
                r.sourceColumn === "" ||
                r.targetColumn === "" ||
                r.sourceTableId === "" ||
                r.targetTableId === "",
            )
          }
          onClick={handleAddRelation}
        />
      </div>

      {relations.length > 0 ? (
        <div className="CreateDatabaseStepThree-relationList">
          {relations.map((relation, index) => (
            <RelationCard
              key={relation.id}
              referenceId={`${databaseCreationInfo.referenceId}`}
              relationNumber={relations.length - index}
              initCollapse={index !== 0}
              relation={relation}
              tables={tables}
              onRemoveRelation={(removeRelationId) => {
                performRelationDeletion(removeRelationId);
              }}
              refreshRelationList={() => {
                setTimeout(loadRelations, 200);
              }}
            />
          ))}
        </div>
      ) : (
        <NoDataMessage message="Aucune relation" />
      )}

      <div className="CreateDatabaseStepThree-footer">
        <Button
          style={{ display: "flex", justifySelf: "flex-end", marginTop: "37px" }}
          className="Btn secondary outlined"
          label="Retour"
          onClick={() => navigateToStep(2)}
        />
        <Button
          style={{ display: "flex", justifySelf: "flex-end", marginTop: "37px" }}
          className="Btn primary"
          label="Terminer"
          onClick={handleDatabaseCreation}
          disabled={
            relations.length > 0 &&
            relations.some(
              (r) =>
                r.sourceColumn === "" ||
                r.targetColumn === "" ||
                r.sourceTableId === "" ||
                r.targetTableId === "",
            )
          }
        />
      </div>
    </div>
  );
}
